import { useMemo, useState } from "react";
import type { BowelMovementPayload, MedicalEvent } from "@medassist/shared";
import { DEFAULT_BM_DAILY_GOAL } from "@medassist/shared";
import { saveBatchEvents, ApiError } from "../../../lib/api.js";
import { PoopIcon } from "../../icons.js";
import { relativeTime } from "../../../lib/time.js";
import type { WidgetProps } from "../types.js";

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

type BowelPresetId = "small" | "normal" | "large" | "diarrhea" | "hard";

interface BowelPresetDef {
  id: BowelPresetId;
  label: string;
  emoji?: string;
  payload: Partial<BowelMovementPayload>;
  matches: (p: Partial<BowelMovementPayload>) => boolean;
}

const BOWEL_PRESETS: Record<BowelPresetId, BowelPresetDef> = {
  small:    { id: "small",    label: "Small",    payload: { volume: "small" },
              matches: (p) => p.volume === "small" },
  normal:   { id: "normal",   label: "Normal",   payload: { volume: "normal" },
              matches: (p) => p.volume === "normal" },
  large:    { id: "large",    label: "Large",    payload: { volume: "large" },
              matches: (p) => p.volume === "large" },
  diarrhea: { id: "diarrhea", label: "Diarrhea", emoji: "🌊",
              payload: { bristolScale: 7, urgency: "urgent" },
              matches: (p) => p.bristolScale === 7 || p.urgency === "urgent" },
  hard:     { id: "hard",     label: "Hard",     emoji: "🪨",
              payload: { bristolScale: 2, straining: true },
              matches: (p) => (p.bristolScale != null && p.bristolScale <= 2) || p.straining === true },
};

// Default display order (also the tie-break order — see rankPresets below).
const PRESET_ORDER: BowelPresetId[] = ["small", "normal", "large", "diarrhea", "hard"];

// Priority order used to classify one historical event into exactly one bucket
// (an event can technically match more than one predicate — e.g. volume "large"
// AND bristolScale 7 — so classification takes the first match in this order,
// diarrhea/hard first since those are the more clinically distinctive signal).
const CLASSIFY_PRIORITY: BowelPresetId[] = ["diarrhea", "hard", "small", "normal", "large"];

const RANKING_WINDOW_DAYS = 90;
const MIN_EVENTS_FOR_WINDOW = 5;
const VISIBLE_BUTTON_COUNT = 3;

type LogDay = "today" | "yesterday";

const LOG_DAY_OPTIONS: { key: LogDay; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
];

function rankPresets(movementEvents: MedicalEvent[]): BowelPresetId[] {
  const cutoff = Date.now() - RANKING_WINDOW_DAYS * 86_400_000;
  const windowed = movementEvents.filter((e) => new Date(e.occurredAt).getTime() >= cutoff);
  const pool = windowed.length >= MIN_EVENTS_FOR_WINDOW ? windowed : movementEvents;

  const counts = new Map<BowelPresetId, number>(PRESET_ORDER.map((id) => [id, 0]));
  for (const e of pool) {
    const p = e.payload as Partial<BowelMovementPayload>;
    const hit = CLASSIFY_PRIORITY.find((id) => BOWEL_PRESETS[id].matches(p));
    if (hit) counts.set(hit, (counts.get(hit) ?? 0) + 1);
  }

  // Array.prototype.sort is stable, and the input is already in PRESET_ORDER,
  // so equal counts naturally keep PRESET_ORDER as the tie-break — no separate
  // tie-break comparison needed.
  return [...PRESET_ORDER]
    .sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0))
    .slice(0, VISIBLE_BUTTON_COUNT);
}

export function BowelWidget({ patient, events, onNavigate, onPrependEvent, onShowToast, onError }: WidgetProps) {
  // A confirmed "didn't go" marker (CATCHUP_WIDGETS_PLAN.md D-CU-4) never counts as
  // a movement (D-CU-18): excluded from days-since-last and the daily-goal count.
  const bms = events.filter(
    (e) => e.eventType === "bowel_movement" && !e.deleted && !(e.payload as { noMovement?: boolean }).noMovement
  );
  const rankedPresets = useMemo(() => rankPresets(bms), [bms]);
  const latest = bms[0];
  const todayCount = bms.filter((e) => isToday(e.occurredAt)).length;
  const goal: number = (patient as unknown as Record<string, unknown>).bmDailyGoal as number ?? DEFAULT_BM_DAILY_GOAL;

  const days = latest ? daysSince(latest.occurredAt) : null;

  const [logDay, setLogDay] = useState<LogDay>("today");

  async function logPreset(id: BowelPresetId) {
    const occurredAt = new Date(logDay === "yesterday" ? Date.now() - 86_400_000 : Date.now()).toISOString();
    const payload: Partial<BowelMovementPayload> & { occurredAt: string } = {
      occurredAt,
      ...BOWEL_PRESETS[id].payload,
    };
    try {
      const [ref] = await saveBatchEvents(patient.patientId, [{ eventType: "bowel_movement", payload }]);
      onPrependEvent(ref, payload);
      onShowToast(ref, `Logged ${BOWEL_PRESETS[id].label.toLowerCase()} movement${logDay === "yesterday" ? " for yesterday" : ""}`);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Couldn't log movement");
    }
  }

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <PoopIcon size={16} className="text-(--accent)" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Bowel / Regularity</h2>
        <button onClick={() => onNavigate("bowel")} className="ml-auto text-xs text-(--accent) hover:underline">
          Details
        </button>
      </div>
      <div className="space-y-2 text-sm">
        {latest ? (
          <div className="flex items-center gap-3">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {days === 0 ? "Today" : `${days}d`}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">since last BM</p>
            </div>
            <p className="flex-1 text-xs text-gray-500 dark:text-gray-400">{relativeTime(latest.occurredAt)}</p>
          </div>
        ) : (
          <p className="text-sm text-gray-400 dark:text-gray-500">No BMs logged yet</p>
        )}
        <div className="flex items-center justify-between border-t border-gray-100 dark:border-gray-800 pt-2 text-sm text-gray-700 dark:text-gray-300">
          <span>Today</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {todayCount} / {goal}
          </span>
        </div>
        <div className="flex items-center gap-2 border-t border-gray-100 dark:border-gray-800 pt-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Log for:</span>
          <div
            className="flex rounded-full bg-gray-100 p-0.5 text-xs font-medium dark:bg-gray-800"
            role="group"
            aria-label="Log day"
          >
            {LOG_DAY_OPTIONS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                aria-pressed={logDay === key}
                onClick={() => setLogDay(key)}
                className={[
                  "min-h-[44px] rounded-full px-3 transition-all sm:min-h-0 sm:py-1",
                  logDay === key
                    ? "bg-[color:var(--accent,#2563eb)] text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-300",
                ].join(" ")}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 pt-1">
          {rankedPresets.map((id) => {
            const preset = BOWEL_PRESETS[id];
            return (
              <button
                key={id}
                onClick={() => void logPreset(id)}
                className="flex min-h-[44px] flex-col items-center justify-center gap-1 rounded-lg border border-gray-200 dark:border-gray-800 p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                title={`Log a ${preset.label.toLowerCase()} movement for ${logDay}`}
              >
                {preset.emoji ? (
                  <span className="text-lg" aria-hidden="true">{preset.emoji}</span>
                ) : (
                  <PoopIcon size={18} />
                )}
                <span className="text-xs font-semibold">{preset.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
